import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { Camera, CheckCircle2, ChevronLeft, CircleHelp, RefreshCw, ScanLine, ShieldCheck, Square, Wifi, WifiOff, XCircle } from "lucide-react";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import "./EventCheckIn.css";

type Outcome = "checked_in" | "already_used" | "wrong_event" | "invalid" | "refunded" | "revoked" | "suspended" | "cancelled" | "payment_unconfirmed" | "event_unavailable";
type CheckInResult = { status: Outcome; checkedInAt?: string | null; attendeeName?: string; raffleNumber?: string };
type ScannerNotice = { kind: "camera" | "offline" | "network" | "auth"; text: string };

const REJECTION_COPY: Record<Exclude<Outcome, "checked_in">, { reason: string; explanation: string }> = {
  already_used: { reason: "Ticket already used", explanation: "A previous check-in is recorded. Do not admit again." },
  wrong_event: { reason: "Wrong event", explanation: "This ticket belongs to a different event. Confirm the event selected above." },
  invalid: { reason: "Invalid ticket", explanation: "The server could not match this code to a valid ticket. Ask the attendee to open their current ticket." },
  refunded: { reason: "Ticket refunded", explanation: "This ticket was refunded and is no longer valid for admission." },
  revoked: { reason: "Ticket revoked", explanation: "This ticket was revoked. Contact the event lead before proceeding." },
  suspended: { reason: "Ticket suspended", explanation: "Admission is paused for this ticket. Contact the event lead." },
  cancelled: { reason: "Ticket cancelled", explanation: "This ticket was cancelled and cannot be admitted." },
  payment_unconfirmed: { reason: "Payment not confirmed", explanation: "Payment has not been confirmed. Do not admit; ask the attendee to wait or contact the event lead." },
  event_unavailable: { reason: "Event unavailable", explanation: "Check-in is unavailable for this event right now. Contact the event lead." },
};
const OUTCOMES: Outcome[] = ["checked_in", "already_used", "wrong_event", "invalid", "refunded", "revoked", "suspended", "cancelled", "payment_unconfirmed", "event_unavailable"];
const QR_REMOVAL_FRAMES = 6;
const SUCCESS_HOLD_MS = 2_100;

export default function EventCheckIn() {
  const events = trpc.eventAdmission.events.useQuery(undefined, { retry: false });
  const scan = trpc.eventAdmission.checkIn.useMutation();
  const auth = useOptionalAuth();
  const [eventSlug, setEventSlug] = useState("");
  const [active, setActive] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [credential, setCredential] = useState("");
  const [notice, setNotice] = useState<ScannerNotice | null>(null);
  const [result, setResult] = useState<CheckInResult | null>(null);
  const [requestPending, setRequestPending] = useState(false);
  const [online, setOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine);
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locked = useRef(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  const decodePaused = useRef(false);
  const lastSubmittedCode = useRef<string | null>(null);
  const noCodeFrames = useRef(0);
  const frameErrorShown = useRef(false);
  const lastCredential = useRef("");
  const resultRef = useRef<CheckInResult | null>(null);

  function stopTracks(media: MediaStream) {
    media.getTracks().forEach(track => {
      if (track.readyState !== "ended") track.stop();
    });
  }

  function stopCamera() {
    generation.current++;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (stream.current) stopTracks(stream.current);
    stream.current = null;
    if (video.current) video.current.srcObject = null;
    if (mounted.current) {
      setActive(false);
      setCameraStarting(false);
    }
  }

  useEffect(() => {
    mounted.current = true;
    const hide = () => { if (document.hidden) stopCamera(); };
    const online = () => setOnline(true);
    const offline = () => setOnline(false);
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      mounted.current = false;
      stopCamera();
      if (successTimer.current) clearTimeout(successTimer.current);
      document.removeEventListener("visibilitychange", hide);
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, []);

  useEffect(() => {
    if (auth && !auth.isLoggedIn) stopCamera();
  }, [auth?.isLoggedIn]);

  function clearVisibleResult() {
    if (successTimer.current) clearTimeout(successTimer.current);
    successTimer.current = null;
    resultRef.current = null;
    setResult(null);
    setNotice(null);
    decodePaused.current = false;
  }

  function clearForNextTicket() {
    clearVisibleResult();
    setCredential("");
    lastCredential.current = "";
    // Keep lastSubmittedCode until the QR has been absent for several frames.
  }

  function authError(error: unknown) {
    const value = error as { data?: { code?: string }; shape?: { data?: { code?: string } } } | null;
    return value?.data?.code === "UNAUTHORIZED" || value?.data?.code === "FORBIDDEN" ||
      value?.shape?.data?.code === "UNAUTHORIZED" || value?.shape?.data?.code === "FORBIDDEN";
  }

  async function submit(raw: string, isManual = false) {
    const value = raw.trim();
    if (!eventSlug || !value || locked.current) return;
    locked.current = true;
    setRequestPending(true);
    decodePaused.current = true;
    lastCredential.current = value;
    lastSubmittedCode.current = value;
    noCodeFrames.current = 0;
    setNotice(null);
    resultRef.current = null;
    setResult(null);
    if (successTimer.current) clearTimeout(successTimer.current);
    if (isManual) setCredential("");
    try {
      if (!online) {
        if (mounted.current) setNotice({ kind: "offline", text: "You’re offline. Check-in has not been confirmed. Reconnect, then retry this ticket." });
        return;
      }
      // The server is the sole authority. Never render approval before this response.
      const response = await scan.mutateAsync({ eventSlug, credential: value }) as CheckInResult;
      if (!OUTCOMES.includes(response.status)) throw new Error("Unexpected check-in response");
      if (mounted.current) {
        resultRef.current = response;
        setResult(response);
        if (response.status === "checked_in") {
          successTimer.current = setTimeout(() => {
            if (!mounted.current || resultRef.current?.status !== "checked_in") return;
            resultRef.current = null;
            setResult(null);
            setNotice(null);
            decodePaused.current = false;
            lastCredential.current = "";
            successTimer.current = null;
          }, SUCCESS_HOLD_MS);
        }
      }
    } catch (error) {
      if (!mounted.current) return;
      if (authError(error)) {
        stopCamera();
        setNotice({ kind: "auth", text: "Your admin session expired. Sign in again before checking in more guests." });
      } else {
        setNotice({
          kind: "network",
          text: "Check-in not confirmed. The server may have received the request; retry explicitly or check this ticket’s status with the event lead.",
        });
      }
    } finally {
      locked.current = false;
      if (mounted.current) setRequestPending(false);
    }
  }

  function scanNext() {
    clearForNextTicket();
  }

  function retryLast() {
    const retryValue = lastCredential.current;
    if (retryValue) {
      clearVisibleResult();
      void submit(retryValue);
    }
  }

  async function start() {
    stopCamera();
    const attempt = generation.current;
    clearVisibleResult();
    noCodeFrames.current = 0;
    frameErrorShown.current = false;
    setNotice(null);
    setCameraStarting(true);
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setCameraStarting(false);
      setNotice({ kind: "camera", text: "Camera scanning needs a secure HTTPS page and camera access. Use Safari or Chrome, or enter the ticket code below." });
      return;
    }
    try {
      const module = await import("jsqr");
      if (!mounted.current || attempt !== generation.current) return;
      const media = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } }, audio: false });
      if (!mounted.current || attempt !== generation.current) { stopTracks(media); return; }
      stream.current = media;
      if (!video.current) { stopCamera(); return; }
      video.current.srcObject = media;
      await video.current.play();
      if (!mounted.current || attempt !== generation.current) { stopTracks(media); return; }
      setActive(true);
      setCameraStarting(false);
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        frameErrorShown.current = true;
        setNotice({ kind: "camera", text: "The camera is on, but this browser can’t read camera frames. Enter the QR contents below or try another browser." });
      }
      const tick = () => {
        if (attempt !== generation.current || !stream.current || !mounted.current) return;
        if (video.current?.readyState === 4 && context) {
          try {
            const ratio = Math.min(1, 800 / video.current.videoWidth);
            canvas.width = Math.max(1, Math.round(video.current.videoWidth * ratio));
            canvas.height = Math.max(1, Math.round(video.current.videoHeight * ratio));
            context.drawImage(video.current, 0, 0, canvas.width, canvas.height);
            const image = context.getImageData(0, 0, canvas.width, canvas.height);
            const code = module.default(image.data, image.width, image.height, { inversionAttempts: "dontInvert" });
            if (code) {
              noCodeFrames.current = 0;
              if (!decodePaused.current && !locked.current && code.data !== lastSubmittedCode.current) {
                void submit(code.data);
              }
            } else {
              noCodeFrames.current++;
              if (noCodeFrames.current >= QR_REMOVAL_FRAMES) lastSubmittedCode.current = null;
            }
          } catch {
            if (mounted.current && !frameErrorShown.current) {
              frameErrorShown.current = true;
              setNotice({ kind: "camera", text: "The camera is on, but this browser can’t read the frame. Use the QR contents field below or restart the camera." });
            }
          }
        }
        timer.current = setTimeout(tick, 180);
      };
      tick();
    } catch (error) {
      stopCamera();
      if (mounted.current && generation.current === attempt + 1) {
        setNotice({
          kind: "camera",
          text: error instanceof Error && error.name === "NotAllowedError"
            ? "Camera permission was denied. Allow camera access in browser settings, or enter the QR contents below."
            : "Couldn’t start the camera. Allow camera permission, close other apps using it, and try again.",
        });
      }
    }
  }

  const timestamp = result?.checkedInAt ? new Date(result.checkedInAt) : null;
  const displayTimestamp = timestamp && !Number.isNaN(timestamp.getTime())
    ? timestamp.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <main className={`event-checkin${result ? " has-result" : ""}`}>
      <header className="event-checkin__header">
        <Link href="/admin" className="event-checkin__back"><ChevronLeft size={19} aria-hidden="true" /> Admin CRM</Link>
        <span className="event-checkin__brand"><ShieldCheck size={17} aria-hidden="true" /> CHI Alliance <span>Door team</span></span>
        <span className={`event-checkin__connection${online ? " is-online" : " is-offline"}`} role="status" aria-label={online ? "Online" : "Offline"}>
          {online ? <Wifi size={15} aria-hidden="true" /> : <WifiOff size={15} aria-hidden="true" />}{online ? "Online" : "Offline"}
        </span>
      </header>
      <section className="event-checkin__panel" aria-labelledby="checkin-title">
        <div className="event-checkin__intro">
          <p className="event-checkin__eyebrow">A warm welcome starts here</p>
          <h1 id="checkin-title">Event check-in</h1>
          <p>Choose the event, point the rear camera at a ticket, and wait for server confirmation.</p>
        </div>

        <div className="event-checkin__event-field">
          <label htmlFor="checkin-event">Checking in guests for</label>
          <select
            id="checkin-event"
            value={eventSlug}
            disabled={active || requestPending}
            onChange={event => {
              setEventSlug(event.target.value);
              clearVisibleResult();
              setNotice(null);
              lastSubmittedCode.current = null;
              lastCredential.current = "";
              noCodeFrames.current = 0;
            }}
          >
            <option value="">Choose an event</option>
            {events.data?.map(event => <option key={event.slug} value={event.slug}>{event.date} · {event.title}</option>)}
          </select>
          {events.isLoading && <p className="event-checkin__event-status" role="status">Loading events…</p>}
          {events.isError && <p className="event-checkin__event-error" role="alert">Events couldn’t be loaded. <button type="button" onClick={() => void events.refetch()}>Try again</button></p>}
          {events.data?.length === 0 && <p className="event-checkin__event-status">No in-person events are available yet.</p>}
        </div>

        {!eventSlug && <div className="event-checkin__choose-hint"><CircleHelp size={18} aria-hidden="true" /> Choose your event to open the scanner.</div>}

        <div className={`event-checkin__camera${active ? " is-active" : ""}`} aria-label="Ticket scanner">
          <video ref={video} playsInline muted autoPlay aria-label="Rear camera ticket scanner" />
          <div className="event-checkin__frame" aria-hidden="true"><span /><span /><span /><span /></div>
          {!active && <div className="event-checkin__placeholder"><ScanLine size={42} aria-hidden="true" /><span>{eventSlug ? "Camera ready" : "Select an event first"}</span></div>}
          {active && !result && !notice && <p className="event-checkin__camera-hint" aria-live="polite">Center one QR code in the frame</p>}
          {requestPending && <div className="event-checkin__camera-wait" role="status">Confirming with CHI Alliance…</div>}
        </div>

        <div className="event-checkin__controls">
          <button className="event-checkin__primary" type="button" disabled={!eventSlug || requestPending} onClick={() => active || cameraStarting ? stopCamera() : void start()}>
            {active || cameraStarting ? <Square size={19} aria-hidden="true" /> : <Camera size={20} aria-hidden="true" />}{active ? "Stop camera" : cameraStarting ? "Cancel camera" : "Start camera"}
          </button>
        </div>

        {notice && (
          <div className={`event-checkin__notice is-${notice.kind}`} role={notice.kind === "network" || notice.kind === "auth" || notice.kind === "offline" ? "alert" : "status"}>
            {notice.kind === "auth" ? <ShieldCheck size={22} aria-hidden="true" /> : notice.kind === "offline" ? <WifiOff size={22} aria-hidden="true" /> : <CircleHelp size={22} aria-hidden="true" />}
            <div className="event-checkin__notice-copy">
              <strong>{notice.kind === "auth" ? "Sign-in required" : notice.kind === "offline" ? "Connection unavailable" : notice.kind === "network" ? "Not confirmed" : "Camera help"}</strong>
              <p>{notice.text}</p>
              {notice.kind === "auth" ? (
                <Link href="/login?returnTo=%2Fevent-check-in" className="event-checkin__text-link">Sign in to continue</Link>
              ) : notice.kind === "network" || notice.kind === "offline" ? (
                <div className="event-checkin__notice-actions">
                  <button type="button" onClick={retryLast} disabled={!online || requestPending || !lastCredential.current}><RefreshCw size={16} aria-hidden="true" /> Retry check-in</button>
                  <button type="button" onClick={scanNext}>Scan next ticket</button>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {result && (
          <section className={`event-checkin__result ${result.status === "checked_in" ? "is-success" : "is-rejected"}`} role={result.status === "checked_in" ? "status" : "alert"} aria-live="assertive" aria-labelledby="checkin-result-title">
            <div className="event-checkin__result-icon" aria-hidden="true">
              {result.status === "checked_in" ? <CheckCircle2 /> : <XCircle />}
            </div>
            {result.status === "checked_in" ? (
              <div className="event-checkin__result-body">
                <p className="event-checkin__result-kicker">CHI Alliance admission</p>
                <h2 id="checkin-result-title">APPROVED</h2>
                <p className="event-checkin__result-status">CHECKED IN</p>
                {result.attendeeName && <p className="event-checkin__attendee">{result.attendeeName}</p>}
                {result.raffleNumber && <p className="event-checkin__raffle">Raffle # <strong>{result.raffleNumber}</strong></p>}
                {displayTimestamp && <p className="event-checkin__time">Checked in at {displayTimestamp}</p>}
                <p className="event-checkin__result-copy">Admission confirmed by the server. This ticket can no longer be used.</p>
                <p className="event-checkin__resume">Ready for the next guest in a moment…</p>
              </div>
            ) : (
              <div className="event-checkin__result-body">
                <p className="event-checkin__result-kicker">Admission decision</p>
                <h2 id="checkin-result-title">NOT ADMITTED</h2>
                <p className="event-checkin__result-status">{REJECTION_COPY[result.status].reason}</p>
                {result.attendeeName && <p className="event-checkin__attendee">{result.attendeeName}</p>}
                {result.raffleNumber && <p className="event-checkin__raffle">Raffle # <strong>{result.raffleNumber}</strong></p>}
                <p className="event-checkin__result-copy">{REJECTION_COPY[result.status].explanation}</p>
                {result.status === "already_used" && displayTimestamp && <p className="event-checkin__time">Previously checked in at {displayTimestamp}</p>}
                <div className="event-checkin__result-actions">
                  <button type="button" onClick={retryLast} disabled={requestPending}><RefreshCw size={17} aria-hidden="true" /> Scan again</button>
                  <button type="button" onClick={scanNext}>Scan next ticket</button>
                </div>
              </div>
            )}
          </section>
        )}

        <details className="event-checkin__manual">
          <summary><span>Camera unavailable?</span> Enter QR contents manually</summary>
          <form onSubmit={event => { event.preventDefault(); void submit(credential, true); }}>
            <label htmlFor="ticket-code">Ticket QR code contents</label>
            <input id="ticket-code" autoComplete="off" spellCheck={false} value={credential} maxLength={200} onChange={event => setCredential(event.target.value)} placeholder="Paste or type ticket code" />
            <button className="event-checkin__secondary" type="submit" disabled={!eventSlug || !credential.trim() || requestPending || !online}>
              Validate with server
            </button>
          </form>
          {!online && <p className="event-checkin__manual-note">Reconnect before validating. Manual codes are never stored.</p>}
        </details>

        <p className="event-checkin__privacy">Admin access only. QR frames stay on this device; only the scanned code is sent to CHI Alliance for server validation.</p>
      </section>
    </main>
  );
}
